/* Standardised product layout — shared behaviours.
   Scoped to elements inside .product-layout; safe to load once per page. */
(function () {
  'use strict';

  function init(root) {
    initAccordions(root);
    initReveal(root);
    initStatCounters(root);
    initGallery(root);
    initCarousel(root);
    initVideoModal(root);
    initInfoModal(root);
    initPackUnitsLabel(root);
    initQuantityStepper(root);
    initCartRow(root);
    initCtaLabel(root);
    initDiscountPrice(root);
    initTiersPriceSync(root);
    initTradePackTable(root);
    initCountdown(root);
    initOrderSummary(root);
    initDescToggle(root);
    initCalloutsTabs(root);
    initDimensionsToggle(root);
  }

  // "Read more" toggle on the hero description box (product-layout-hero.liquid).
  function initDescToggle(root) {
    root.querySelectorAll('[data-pl-desc-toggle]').forEach(function (btn) {
      if (btn.dataset.plDescBound) return;
      btn.dataset.plDescBound = 'true';
      var more = btn.previousElementSibling;
      if (!more) return;
      btn.addEventListener('click', function () {
        var isHidden = more.hidden;
        more.hidden = !isHidden;
        btn.textContent = isHidden ? btn.dataset.labelLess : btn.dataset.labelMore;
      });
    });
  }

  // Mobile "Engineered in detail" callouts diagram (product-layout-callouts.liquid,
  // plain style): only one callout is ever visible on mobile at a time — tapping a
  // name in the .pl-callouts-mobile-list swaps which dot/line/label carries
  // .is-active (matched by data-pl-callout-panel index) instead of showing all 8
  // at once. No-op (nothing to select) on pages/breakpoints without this list.
  function initCalloutsTabs(root) {
    root.querySelectorAll('.pl-callouts-mobile-list').forEach(function (list) {
      if (list.dataset.plCalloutsBound) return;
      list.dataset.plCalloutsBound = 'true';
      var stage = list.closest('.pl-callouts-stage') || list.parentElement;
      var tabs = Array.prototype.slice.call(list.querySelectorAll('[data-pl-callout-tab]'));
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.getAttribute('data-pl-callout-tab');
          tabs.forEach(function (t) {
            var active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          stage.querySelectorAll('[data-pl-callout-panel]').forEach(function (panel) {
            panel.classList.toggle('is-active', panel.getAttribute('data-pl-callout-panel') === key);
          });
        });
      });
    });
  }

  // Dimensions section throat toggle (product-layout-dimensions.liquid, added
  // for InnoSkim's Standard/Extended drawings): swaps which drawing image is
  // shown, which panel title/tag text displays, and which "a"/"b"-tagged rows
  // are visible — rows with no throat tag are untouched (they show either way).
  function initDimensionsToggle(root) {
    root.querySelectorAll('.pl-dimensions[data-pl-dims-active]').forEach(function (wrap) {
      if (wrap.dataset.plDimsBound) return;
      wrap.dataset.plDimsBound = 'true';

      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[data-pl-dims-tab]'));
      var images = Array.prototype.slice.call(wrap.querySelectorAll('[data-pl-dims-image]'));
      var titleEl = wrap.querySelector('[data-pl-dims-title]');
      var tagEl = wrap.querySelector('[data-pl-dims-tag]');

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.getAttribute('data-pl-dims-tab');
          wrap.setAttribute('data-pl-dims-active', key);
          tabs.forEach(function (t) {
            var active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          images.forEach(function (img) {
            img.classList.toggle('is-active', img.getAttribute('data-pl-dims-image') === key);
          });
          if (titleEl) titleEl.textContent = titleEl.getAttribute('data-title-' + key) || '';
          if (tagEl) tagEl.textContent = tagEl.getAttribute('data-tag-' + key) || '';
        });
      });
    });
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.settings) {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function updateDiscountPriceBox(box, variantPriceCents) {
    var pct = parseFloat(box.getAttribute('data-pl-discount-pct')) || 0;
    var discounted = Math.round(variantPriceCents * (100 - pct) / 100);
    var priceEl = box.querySelector('[data-pl-price]');
    var compareEl = box.querySelector('[data-pl-price-compare]');
    if (priceEl) priceEl.textContent = formatMoney(discounted);
    if (compareEl) compareEl.textContent = formatMoney(variantPriceCents);
    // Keep the cached per-unit/RRP cents in sync so the order-summary total (which
    // reads these same data attributes) stays correct after a size change.
    box.dataset.plUnitPriceCents = discounted;
    box.dataset.plRrpCents = variantPriceCents;
  }

  // Builder & Distributor Pricing panel (product-layout-pricing-tiers.liquid)
  // and the final-cta price note both render a server-side snapshot of
  // current_variant.price — accurate on first load, but stale the moment a
  // shopper switches variant (e.g. InnoSkim's Standard/Extended throat),
  // since neither lives inside .pl-buybox (initDiscountPrice's scope) nor
  // participates in that scoped lookup. Runs globally, unscoped, since only
  // one product/variant is ever selected per page.
  function initTiersPriceSync() {
    if (document.body.dataset.plTiersPriceSyncBound) return;
    document.body.dataset.plTiersPriceSyncBound = 'true';

    document.addEventListener('variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      document.querySelectorAll('[data-pl-tiers-price]').forEach(function (el) {
        el.textContent = formatMoney(variant.price);
      });
      document.querySelectorAll('[data-pl-per-body]').forEach(function (el) {
        var divisor = parseFloat(el.getAttribute('data-pl-per-body-divisor')) || 0;
        if (divisor > 0) el.textContent = formatMoney(Math.round(variant.price / divisor));
      });
    });
  }

  function initDiscountPrice(root) {
    // Runs once globally, not per .product-layout root, since the listener is on document.
    if (document.body.dataset.plDiscountPriceBound) return;
    document.body.dataset.plDiscountPriceBound = 'true';

    document.addEventListener('variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) return;
      // Update every buybox on the page, not just whichever one held focus.
      // Previously this scoped to document.activeElement.closest('.pl-buybox')
      // (the offer panel sits as a sibling BEFORE #ProductSection-... in
      // product-layout-hero.liquid, so .pl-buybox is the nearest ancestor
      // containing both it and the size selector) — but a product only ever
      // has one selected variant, so every buybox on the page should reflect
      // it identically anyway. Relying on activeElement was also fragile on
      // touch devices: tapping a pack-size radio/label doesn't reliably move
      // focus into it on mobile Safari the way a desktop click does, so
      // .closest('.pl-buybox') silently returned null and this whole handler
      // no-opped — the price (and the order-summary total computed from its
      // now-stale cents attributes) never updated after switching pack size
      // on a phone.
      document.querySelectorAll('.pl-buybox').forEach(function (buybox) {
        var box = buybox.querySelector('[data-pl-price-box]');
        if (box) updateDiscountPriceBox(box, variant.price);
        computeOrderSummary(buybox);
      });
    });
  }

  // Live countdown (sticky banner + offer panel): both read the same
  // data-pl-sale-deadline ISO string, set from the section's Customizer setting.
  function formatCountdown(ms) {
    if (ms <= 0) return 'Offer ended';
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var d = Math.floor(ms / 86400000);
    var h = Math.floor(ms / 3600000) % 24;
    var m = Math.floor(ms / 60000) % 60;
    var s = Math.floor(ms / 1000) % 60;
    return (d > 0 ? d + 'd ' : '') + pad(h) + 'h ' + pad(m) + 'm ' + pad(s) + 's';
  }

  function initCountdown(root) {
    var els = root.querySelectorAll('[data-pl-countdown]');
    if (!els.length) return;
    function tick() {
      els.forEach(function (el) {
        var deadline = Date.parse(el.getAttribute('data-pl-sale-deadline'));
        if (isNaN(deadline)) return;
        el.textContent = formatCountdown(deadline - Date.now());
      });
    }
    tick();
    setInterval(tick, 1000);
  }

  function ordinal(n) {
    var suffixes = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  }

  // Live order total (product-layout-hero.liquid): recomputes whenever quantity
  // or the selected variant (size) changes, using the same discount-cap math the
  // real Shopify automatic discount applies, so the displayed total always
  // matches what checkout will actually charge.
  function computeOrderSummary(buybox) {
    var box = buybox.querySelector('[data-pl-price-box]');
    var summary = buybox.querySelector('[data-pl-order-summary]');
    if (!box || !summary) return;

    var unitCents = parseFloat(box.dataset.plUnitPriceCents) || 0;
    var rrpCents = parseFloat(box.dataset.plRrpCents) || unitCents;
    var cap = parseInt(box.dataset.plDiscountCap, 10) || 0;
    var pct = box.dataset.plDiscountPct || '';
    var qtyInput = buybox.querySelector('.product__quantity input[type="number"]');
    var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    var discountedUnits = Math.min(qty, cap);
    var fullUnits = Math.max(0, qty - cap);
    var total = discountedUnits * unitCents + fullUnits * rrpCents;

    var totalEl = summary.querySelector('[data-pl-order-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);

    var overCap = pct > 0 && cap > 0 && qty > cap;
    var noteEl = summary.querySelector('[data-pl-order-note]');
    if (noteEl) {
      if (overCap) {
        var template = summary.dataset.plCapNote || '';
        noteEl.textContent = template
          .replace(/\{\{\s*cap\s*\}\}/g, cap)
          .replace(/\{\{\s*pct\s*\}\}/g, pct)
          .replace(/\{\{\s*next\s*\}\}/g, ordinal(cap + 1));
        noteEl.hidden = false;
      } else {
        noteEl.hidden = true;
      }
    }

    // The Add to cart label/data attributes live on the .product-section div
    // (a descendant of .pl-buybox, not the buybox itself), since that's where
    // theme.js's Product component and initCtaLabel already read from.
    var ctaWrap = buybox.querySelector('.product-section[data-pl-cta-label]');
    var span = buybox.querySelector('[data-add-to-cart-text]');
    if (span && ctaWrap) {
      var label = overCap ? ctaWrap.dataset.plCtaLabelOverCap : ctaWrap.dataset.plCtaLabel;
      if (label) {
        span.setAttribute('data-default-text', label);
        if (!span.closest('[disabled]')) span.textContent = label;
      }
    }
  }

  function initOrderSummary(root) {
    root.querySelectorAll('.pl-buybox').forEach(function (buybox) {
      var summary = buybox.querySelector('[data-pl-order-summary]');
      if (!summary || buybox.dataset.plOrderSummaryBound) return;
      buybox.dataset.plOrderSummaryBound = 'true';

      computeOrderSummary(buybox);

      buybox.addEventListener('change', function (evt) {
        if (evt.target.matches('.product__quantity input[type="number"]')) {
          computeOrderSummary(buybox);
        }
      });
    });
  }

  // Trade pack pricing table (product-layout-trade-hero.liquid): pack tier is a
  // real variant option (option2), so switching size (option1) via variant:change
  // needs the sibling pack-tier variants (not just the one theme.js resolves) to
  // refresh all three rows' unit prices — pulled from the same [data-variant-json]
  // textarea product-form.liquid already renders for every buy box.
  function initTradePackTable(root) {
    root.querySelectorAll('[data-pl-trade-buybox]').forEach(function (container) {
      if (container.dataset.plTradeBound) return;
      container.dataset.plTradeBound = 'true';

      var variantsEl = container.querySelector('[data-variant-json]');
      var variants = null;
      try { variants = variantsEl && JSON.parse(variantsEl.textContent); } catch (e) { variants = null; }
      if (!variants) return;

      var rows = container.querySelectorAll('.pl-trade-pack__row');

      function recompute() {
        var checked = container.querySelector('.pl-trade-pack__row input:checked');
        var summaryEl = container.querySelector('[data-pl-order-summary]');
        var totalEl = container.querySelector('[data-pl-order-total]');
        if (!checked || !summaryEl || !totalEl) return;
        var row = checked.closest('.pl-trade-pack__row');
        var units = parseInt(row.dataset.plPackUnits, 10) || 1;
        var priceCents = parseFloat(row.dataset.plPackPriceCents) || 0;
        var qtyInput = container.querySelector('.product__quantity input[type="number"]');
        var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        summaryEl.textContent = qty + (qty > 1 ? ' packs' : ' pack') + ' — ' + (units * qty) + ' valve' + (units * qty > 1 ? 's' : '') + ' total';
        totalEl.textContent = formatMoney(priceCents * qty);
      }

      function updatePackPricesForSize(size) {
        rows.forEach(function (row) {
          var packValue = row.dataset.value;
          var units = parseInt(row.dataset.plPackUnits, 10) || 1;
          var match = null;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].option1 === size && variants[i].option2 === packValue) { match = variants[i]; break; }
          }
          if (!match) return;
          row.dataset.plPackPriceCents = match.price;
          var priceEl = row.querySelector('[data-pl-pack-price]');
          if (priceEl) {
            priceEl.textContent = units > 1
              ? formatMoney(Math.round(match.price / units)) + '/unit'
              : formatMoney(match.price);
          }
        });
        recompute();
      }

      // Seed each row's price-in-cents from the server-rendered variant this
      // section loaded with, so the summary/total is correct before any change fires.
      rows.forEach(function (row) {
        var size = container.querySelector('.variant-input input:checked');
        size = size ? size.value : null;
        var packValue = row.dataset.value;
        var units = parseInt(row.dataset.plPackUnits, 10) || 1;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].option1 === size && variants[i].option2 === packValue) {
            row.dataset.plPackPriceCents = variants[i].price;
            break;
          }
        }
        if (!row.dataset.plPackPriceCents) row.dataset.plPackPriceCents = '0';
      });
      recompute();

      container.addEventListener('change', function (evt) {
        if (evt.target.matches('.pl-trade-pack__row input, .product__quantity input[type="number"]')) {
          recompute();
        }
      });

      document.addEventListener('variant:change', function (evt) {
        var variant = evt.detail && evt.detail.variant;
        if (!variant) return;
        var active = document.activeElement;
        var activeContainer = active && active.closest ? active.closest('[data-pl-trade-buybox]') : null;
        if (activeContainer !== container) return;
        updatePackPricesForSize(variant.option1);
      });
    });
  }

  function initQuantityStepper(root) {
    root.querySelectorAll('.pl-buybox .product__quantity input[type="number"]').forEach(function (input) {
      if (input.dataset.plEnhanced) return;
      input.dataset.plEnhanced = 'true';

      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'pl-qty-btn pl-qty-btn--minus';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.textContent = '−';

      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'pl-qty-btn pl-qty-btn--plus';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.textContent = '+';

      input.parentNode.insertBefore(minus, input);
      input.parentNode.appendChild(plus);

      var step = function (delta) {
        var min = parseInt(input.min, 10) || 1;
        var value = (parseInt(input.value, 10) || min) + delta;
        input.value = Math.max(min, value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      minus.addEventListener('click', function () { step(-1); });
      plus.addEventListener('click', function () { step(1); });
    });
  }

  function initCartRow(root) {
    root.querySelectorAll('.pl-buybox form').forEach(function (form) {
      var qty = form.querySelector('.product__quantity');
      var btn = form.querySelector('[data-add-to-cart]');
      if (!qty || !btn || qty.dataset.plWrapped) return;
      qty.dataset.plWrapped = 'true';
      var row = document.createElement('div');
      row.className = 'pl-cart-row';
      qty.parentNode.insertBefore(row, qty);
      row.appendChild(qty);
      row.appendChild(btn);
    });
  }

  function initCtaLabel(root) {
    root.querySelectorAll('.product-section[data-pl-cta-label]').forEach(function (wrap) {
      var label = wrap.dataset.plCtaLabel;
      if (!label) return;
      var span = wrap.querySelector('[data-add-to-cart-text]');
      if (!span) return;
      // Overwrite data-default-text too: theme.js resets the button's text from
      // this attribute whenever the selected variant becomes available again
      // (e.g. after switching off a sold-out size), which would otherwise wipe
      // out this custom label on the next variant change.
      span.setAttribute('data-default-text', label);
      if (!span.closest('[disabled]')) span.textContent = label;
    });
  }

  function initAccordions(root) {
    root.querySelectorAll('.pl-accordion').forEach(function (group) {
      // FAQ columns (Mildred-style): each item toggles independently, several
      // can be open at once. Everywhere else (e.g. the hero's own accordion):
      // exclusive, opening one closes any other open item in the same group.
      var exclusive = !group.closest('.pl-faq');
      group.querySelectorAll('.pl-accordion__item').forEach(function (item) {
        var trigger = item.querySelector('.pl-accordion__trigger');
        var mark = item.querySelector('.pl-accordion__mark');
        if (!trigger || trigger.dataset.plAccordionBound) return;
        trigger.dataset.plAccordionBound = 'true';
        trigger.addEventListener('click', function () {
          var willOpen = !item.classList.contains('is-open');
          if (exclusive) {
            group.querySelectorAll('.pl-accordion__item.is-open').forEach(function (open) {
              open.classList.remove('is-open');
              var openMark = open.querySelector('.pl-accordion__mark');
              if (openMark) openMark.textContent = '+';
            });
          }
          item.classList.toggle('is-open', willOpen);
          if (mark) mark.textContent = willOpen ? '−' : '+';
        });
      });
    });
  }

  function initReveal(root) {
    var targets = root.querySelectorAll('[data-pl-reveal], .pl-stat, .pl-quote, .pl-card');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -30px 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  function initStatCounters(root) {
    root.querySelectorAll('[data-pl-count-to]').forEach(function (el) {
      var target = parseInt(el.getAttribute('data-pl-count-to'), 10);
      var prefix = el.getAttribute('data-pl-count-prefix') || '';
      if (!target || !('IntersectionObserver' in window)) return;
      var done = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !done) {
            done = true;
            io.disconnect();
            var t0 = performance.now();
            var duration = 1600;
            var step = function (t) {
              var p = Math.min(1, (t - t0) / duration);
              var eased = 1 - Math.pow(1 - p, 3);
              el.textContent = prefix + Math.round(target * eased).toLocaleString('en-US') + '+';
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      }, { threshold: 0.3 });
      io.observe(el);
    });
  }

  function initGallery(root) {
    root.querySelectorAll('.pl-gallery').forEach(function (gallery) {
      var main = gallery.querySelector('.pl-gallery__main');
      if (!main) return;
      var images = Array.prototype.slice.call(main.querySelectorAll('img[data-pl-full]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('.pl-gallery__thumb'));
      if (!images.length) return;

      var prevBtn = main.querySelector('[data-pl-gallery-prev]');
      var nextBtn = main.querySelector('[data-pl-gallery-next]');
      var zoomBtn = main.querySelector('[data-pl-gallery-zoom]');
      var counter = main.querySelector('[data-pl-gallery-counter]');
      var current = 0;

      function setActive(i) {
        current = (i + images.length) % images.length;
        images.forEach(function (img, j) { img.classList.toggle('is-active', j === current); });
        thumbs.forEach(function (t, j) { t.classList.toggle('is-active', j === current); });
        if (counter) counter.textContent = (current + 1) + ' / ' + images.length;
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { setActive(i); });
      });
      if (prevBtn) prevBtn.addEventListener('click', function () { setActive(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { setActive(current + 1); });
      if (zoomBtn) {
        zoomBtn.addEventListener('click', function () { openGalleryLightbox(images, current); });
      }

      // Exposed so the mobile carousel (a separate DOM block, hidden on desktop
      // via CSS rather than removed) can open the same lightbox at the tapped index.
      gallery.__plGalleryImages = images;
    });
  }

  function initCarousel(root) {
    root.querySelectorAll('.pl-carousel').forEach(function (carousel) {
      var track = carousel.querySelector('.pl-carousel__track');
      var slides = carousel.querySelectorAll('.pl-carousel__slide');
      var dots = carousel.querySelectorAll('.pl-carousel__dot');
      var counter = carousel.querySelector('.pl-carousel__counter');
      if (!track || !slides.length) return;

      function setActive(i) {
        dots.forEach(function (d, j) { d.classList.toggle('is-active', j === i); });
        if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
        });
      });

      var scrollTimer;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var index = Math.round(track.scrollLeft / track.clientWidth);
          setActive(Math.max(0, Math.min(slides.length - 1, index)));
        }, 80);
      });

      setActive(0);

      // Additive: tap a slide's photo to open the same lightbox the desktop
      // gallery uses, starting at that slide's index. Does not touch the
      // swipe/dot/counter behaviour above.
      var gallery = carousel.parentElement && carousel.parentElement.querySelector('.pl-gallery');
      var images = gallery ? gallery.__plGalleryImages : null;
      if (images && images.length) {
        carousel.querySelectorAll('[data-pl-carousel-zoom]').forEach(function (btn, i) {
          btn.addEventListener('click', function () { openGalleryLightbox(images, i); });
        });
      }
    });
  }

  // Thin adapter around the theme's bundled PhotoSwipe v4 (see
  // snippets/photoswipe-template.liquid, loaded globally in layout/theme.liquid)
  // rather than duplicating lightbox/zoom/pan/pinch/keyboard-nav logic — those
  // all come from PhotoSwipe itself. We only add: the starting item list, and
  // zoom in/out buttons + wheel-zoom, which this theme's bundle doesn't wire up.
  function openGalleryLightbox(images, startIndex) {
    var pswpElement = document.querySelectorAll('.pswp')[0];
    if (!pswpElement || typeof PhotoSwipe === 'undefined' || typeof PhotoSwipeUI_Default === 'undefined') return;

    var items = images.map(function (img) {
      return {
        src: img.getAttribute('data-pl-full') || img.currentSrc || img.src,
        w: parseInt(img.getAttribute('data-pl-full-w'), 10) || 0,
        h: parseInt(img.getAttribute('data-pl-full-h'), 10) || 0,
        msrc: img.currentSrc || img.src
      };
    });

    var options = {
      index: startIndex || 0,
      history: false,
      // This theme's .pswp shell (snippets/photoswipe-template.liquid) only
      // renders prev/close/next buttons — no caption, counter or preloader
      // markup — so those UI features must be disabled or PhotoSwipeUI_Default
      // throws trying to manipulate elements that don't exist (matches how
      // the theme's own theme.Photoswipe class configures these same options).
      captionEl: false,
      counterEl: false,
      preloaderEl: false,
      shareEl: false,
      closeOnScroll: false,
      bgOpacity: 0.9,
      getThumbBoundsFn: function (index) {
        var thumb = images[index];
        var pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
        var rect = thumb.getBoundingClientRect();
        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
      }
    };

    var pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    initPswpZoomControls(pswpElement, pswp);
    pswp.init();
  }

  // Injects zoom in/out buttons into the (already-rendered, shared) .pswp__ui
  // and wires wheel/trackpad zoom — PhotoSwipe v4 ships pan, pinch, swipe,
  // keyboard nav and Escape-to-close natively, but not incremental wheel-zoom
  // or a zoom button, so those two pieces are hand-added here.
  function initPswpZoomControls(pswpElement, pswp) {
    var ui = pswpElement.querySelector('.pswp__ui');
    if (!ui) return;

    var controls = ui.querySelector('.pl-pswp-zoom-controls');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'pl-pswp-zoom-controls';
      controls.innerHTML =
        '<button type="button" class="pl-pswp-zoom-btn" data-pl-pswp-zoom-out aria-label="Zoom out">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path fill="currentColor" d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>' +
        '</button>' +
        '<button type="button" class="pl-pswp-zoom-btn" data-pl-pswp-zoom-in aria-label="Zoom in">' +
          '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path fill="currentColor" d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>' +
        '</button>';
      ui.appendChild(controls);
    }

    function zoomBy(factor) {
      if (!pswp.currItem) return;
      var min = pswp.currItem.initialZoomLevel;
      var max = Math.max(min * 4, 1);
      var level = Math.min(Math.max(pswp.getZoomLevel() * factor, min), max);
      var center = { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 };
      pswp.zoomTo(level, center, 200);
    }

    var zoomInBtn = controls.querySelector('[data-pl-pswp-zoom-in]');
    var zoomOutBtn = controls.querySelector('[data-pl-pswp-zoom-out]');
    zoomInBtn.addEventListener('click', function () { zoomBy(1.5); });
    zoomOutBtn.addEventListener('click', function () { zoomBy(1 / 1.5); });

    function onWheel(e) {
      if (!pswp.currItem) return;
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15);
    }
    pswpElement.addEventListener('wheel', onWheel, { passive: false });
    pswp.listen('close', function () {
      pswpElement.removeEventListener('wheel', onWheel);
      controls.remove();
    });
  }

  function initVideoModal(root) {
    root.querySelectorAll('.pl-video-frame').forEach(function (frame) {
      var modalId = frame.getAttribute('data-pl-video-modal');
      var modal = modalId ? root.querySelector('#' + modalId) : null;
      if (!modal) return;
      var mediaHolder = modal.querySelector('[data-pl-video-media]');
      var videoUrl = frame.getAttribute('data-pl-video-url');

      frame.addEventListener('click', function () {
        if (mediaHolder && videoUrl && !mediaHolder.dataset.loaded) {
          mediaHolder.innerHTML = buildEmbed(videoUrl);
          mediaHolder.dataset.loaded = 'true';
        }
        modal.classList.add('is-open');
      });

      modal.querySelectorAll('[data-pl-video-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          modal.classList.remove('is-open');
          var video = mediaHolder ? mediaHolder.querySelector('video') : null;
          if (video) video.pause();
          var iframe = mediaHolder ? mediaHolder.querySelector('iframe') : null;
          if (iframe) iframe.src = iframe.src; // stop playback by reloading
        });
      });
      modal.addEventListener('click', function (e) {
        if (e.target !== modal) return;
        modal.classList.remove('is-open');
        var video = mediaHolder ? mediaHolder.querySelector('video') : null;
        if (video) video.pause();
        var iframe = mediaHolder ? mediaHolder.querySelector('iframe') : null;
        if (iframe) iframe.src = iframe.src;
      });
    });
  }

  // Generic content modal (e.g. the "Why?" info panel on
  // product-layout-calculator.liquid) — same open/close pattern as
  // initVideoModal above, minus the embed-building: trigger elements carry
  // data-pl-info-modal="<id>", the panel itself carries that id plus
  // .pl-info-modal, and any element inside it with data-pl-info-close
  // closes it, as does a click on the backdrop itself.
  function initInfoModal(root) {
    root.querySelectorAll('[data-pl-info-modal]').forEach(function (trigger) {
      if (trigger.dataset.plInfoBound) return;
      trigger.dataset.plInfoBound = 'true';
      var modalId = trigger.getAttribute('data-pl-info-modal');
      var modal = modalId ? root.querySelector('#' + modalId) : null;
      if (!modal) return;

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        modal.classList.add('is-open');
      });
      modal.querySelectorAll('[data-pl-info-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          modal.classList.remove('is-open');
        });
      });
      modal.addEventListener('click', function (e) {
        if (e.target !== modal) return;
        modal.classList.remove('is-open');
      });
    });
  }

  // "1 pack = X units" price-row label (product-layout-hero.liquid, opt-in via
  // show_pack_units_label) — reads the unit count from the leading number in
  // the currently selected variant's first option value (e.g. "8 Pack" -> 8,
  // "24 Pack" -> 24), so it needs no per-block schema setting of its own.
  // Recomputes on both quantity input changes and the theme's global
  // variant:change event (same event initDiscountPrice listens for).
  function initPackUnitsLabel(root) {
    root.querySelectorAll('[data-pl-pack-units-label]').forEach(function (label) {
      if (label.dataset.plUnitsBound) return;
      label.dataset.plUnitsBound = 'true';
      var qtyInput = document.getElementById(label.getAttribute('data-pl-qty-input'));
      var unitNoun = label.getAttribute('data-pl-unit-noun') || 'units';
      var unitsPerPack = parseInt(label.getAttribute('data-pl-units-per-pack'), 10) || 0;

      function render() {
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var total = unitsPerPack * qty;
        label.textContent = qty + (qty > 1 ? ' packs = ' : ' pack = ') + total + ' ' + unitNoun;
      }

      if (qtyInput) {
        // 'input' covers typing directly into the field; the +/- stepper buttons
        // (initQuantitySteppers, above) set .value programmatically and dispatch
        // 'change' instead, which 'input' alone doesn't catch — without this the
        // label only updates when the field is edited by hand, not via the
        // stepper buttons everyone actually uses.
        qtyInput.addEventListener('input', render);
        qtyInput.addEventListener('change', render);
      }
      document.addEventListener('variant:change', function (evt) {
        var variant = evt.detail && evt.detail.variant;
        if (!variant || !variant.option1) return;
        var parsed = parseInt(variant.option1, 10);
        if (!isNaN(parsed)) unitsPerPack = parsed;
        render();
      });
      render();
    });
  }

  function buildEmbed(url) {
    var yt = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([\w-]+)/);
    var vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (yt) {
      return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0" allow="autoplay; encrypted-media" allowfullscreen></iframe>';
    }
    if (vimeo) {
      return '<iframe src="https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1" allow="autoplay; fullscreen" allowfullscreen></iframe>';
    }
    return '<video src="' + url + '" controls autoplay playsinline></video>';
  }

  document.querySelectorAll('.product-layout').forEach(init);
})();
